import { Component, Input, Output, EventEmitter } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { FormlyFieldConfig } from "@ngx-formly/core";
import {
    ErrorDiscovery,
    ErrorRecord,
} from "src/app/model/error-record/ErrorRecord";
import { ErrorCategoryPipe } from "src/app/pipe/error-category.pipe";
import { Policies } from "src/constants";
import { ErrorHeader } from "src/app/model/error-record/ErrorCall";
import { LoginService } from "src/app/service/login.service";
import { FormlyFormOptions, FormlyModule } from "@ngx-formly/core";
import { CommonModule } from "@angular/common";
import { FormlyMaterialModule } from "@ngx-formly/material";
import { FormlyMatCheckboxModule } from "@ngx-formly/material/checkbox";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatIconModule } from "@angular/material/icon";
import { FormsModule } from "@angular/forms";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/state";
import { ReasoningGroupComponent } from "src/app/components/reasoning/reasoning-group/reasoning-group.component";
import { ReasoningItemConfigs } from "src/app/model/reasoning/ReasoningType";
import * as ReasoningActions from "src/app/state/reasoning/reasoning.actions";

@Component({
    standalone: true,
    imports: [
        CommonModule,
        FormlyModule,
        ReactiveFormsModule,
        FormlyMaterialModule,
        FormlyMatCheckboxModule,
        MatButtonModule,
        MatInputModule,
        MatFormFieldModule,
        MatSelectModule,
        MatIconModule,
        FormsModule,
        ReasoningGroupComponent,
    ],
    selector: "app-error-update-form",
    templateUrl: "./error-update-form.component.html",
    styleUrls: ["./error-update-form.component.scss"],
})
export class ErrorUpdateFormComponent {
    @Input() set initialData(value: ErrorRecord) {
        if (!value) {
            return;
        }
        const isFirstLoad = !this.data;
        this.data = value;

        // Only initialize form on first load, not on subsequent updates (like after failed submission)
        if (isFirstLoad) {
            this.initWithErrorRecord(this.data);
        } else {
            // Update only the discoveries for validation errors, but keep the form model intact
            this.updateDiscoveriesOnly(this.data);
        }
    }
    @Input() errorIsArchived: boolean;
    @Output() updatedError: EventEmitter<ErrorRecord> =
        new EventEmitter<ErrorRecord>();
    @Output() archiveError: EventEmitter<ErrorRecord> =
        new EventEmitter<ErrorRecord>();
    @Output() unarchiveError: EventEmitter<ErrorRecord> =
        new EventEmitter<ErrorRecord>();
    @Output() sendEmail: EventEmitter<ErrorRecord> =
        new EventEmitter<ErrorRecord>();
    @Output() cancel: EventEmitter<void> = new EventEmitter<void>();

    data: ErrorRecord;
    // Discoveries. First all of them, and then split up into different types
    discoveries: ErrorDiscovery[];
    headerDiscoveries: ErrorDiscovery[];
    pathDiscoveries: ErrorDiscovery[];
    payloadDiscoveries: ErrorDiscovery[];

    // Error categories, broken out of their discoveries
    errorCategories: string[];

    form = new FormGroup({});
    model: any = {};
    fields: FormlyFieldConfig[];
    formlyOptions: FormlyFormOptions = {};
    isFieldEditingEnabled = false;

    readonly reasoningItems = [ReasoningItemConfigs.Correction];

    constructor(
        private errorCategoryPipe: ErrorCategoryPipe,
        private loginService: LoginService,
        private store: Store<AppState>,
    ) {}

    ngAfterViewInit() {
        // The validation errors in the form do not show until
        // the field is touched. We want to show the errors straight away,
        // so we touch all fields right form the start.
        this.form.markAllAsTouched();

        // Disable remove buttons on initial load - try multiple times to catch dynamic rendering
        setTimeout(() => this.disableRemoveButtons(), 0);
        setTimeout(() => this.disableRemoveButtons(), 100);
        setTimeout(() => this.disableRemoveButtons(), 300);

        // Set up observer to watch for dynamically added buttons
        this.observeFormChanges();
    }

    private observeFormChanges() {
        const formElement = document.querySelector("formly-form");
        if (!formElement) return;

        const observer = new MutationObserver(() => {
            if (!this.isFieldEditingEnabled) {
                this.disableRemoveButtons();
            }
        });

        observer.observe(formElement, {
            childList: true,
            subtree: true,
        });
    }

    private disableRemoveButtons() {
        // Find ALL buttons within the payload fields area
        const allButtons = document.querySelectorAll(
            "formly-field button, .payload-field-group button, formly-group button",
        );

        allButtons.forEach((btn: any) => {
            // Disable all icon buttons that might be remove buttons
            if (btn.tagName === "BUTTON" && btn.type === "button") {
                btn.disabled = !this.isFieldEditingEnabled;
                btn.style.opacity = !this.isFieldEditingEnabled ? "0.3" : "1";
                btn.style.cursor = !this.isFieldEditingEnabled
                    ? "not-allowed"
                    : "pointer";
                btn.style.pointerEvents = !this.isFieldEditingEnabled
                    ? "none"
                    : "auto";
            }
        });
    }

    /**
     * Creates the Formly-form, with all prefilled data
     * and validation errors from the ErrorRecord.
     */
    private initWithErrorRecord(data: ErrorRecord) {
        this.errorCategories = Array.from(
            new Set<string>(
                this.data.discoveries.map((d: ErrorDiscovery) => {
                    const transformedCategory =
                        this.errorCategoryPipe.transform(d.errorCategory);
                    return transformedCategory as string;
                }),
            ),
        );
        this.setDiscoveries(this.data.discoveries);

        // Flatten payload to check for nested/array properties
        const flattenedPayload = this.flattenObject(this.data.payload.payload);
        const missingDiscoveries = this.data.discoveries.filter((d) => {
            return (
                d.location?.payloadFieldPath &&
                !(d.location.payloadFieldPath in flattenedPayload)
            );
        });

        this.fields = [
            this.createSubHeader("HTTP Call Details"),
            ...this.createCallFields(),
            this.createSubHeader("HTTP Headers"),
            ...this.headersToFormlyFields(),
            ...this.discoveriesToFormlyFields(missingDiscoveries),
            this.createSubHeader("Request Payload"),
            ...this.payloadToFormlyFields(),
        ];

        this.model = JSON.parse(
            JSON.stringify({
                // probably something wrong, since this is needed
                headers: this.headersToFormlyModel(this.data.call.headers),
                payloadFields: this.payloadToFormlyModel(
                    this.data.payload.payload,
                ),
                endpoint: this.data.call.endpoint,
                method: this.data.call.method,
            }),
        );
    }

    private setDiscoveries(discoveries: ErrorDiscovery[]) {
        // Split the discoveries up into different categories,
        // Depending on what fields they refer to.
        this.discoveries = discoveries;
        this.headerDiscoveries = this.discoveries.filter((d) => {
            return d.location?.headerName;
        });
        this.pathDiscoveries = this.discoveries.filter((d) => {
            return d.location?.pathPart;
        });
        this.payloadDiscoveries = this.discoveries.filter((d) => {
            return d.location?.payloadFieldPath;
        });
    }

    private updateDiscoveriesOnly(data: ErrorRecord) {
        // Update discoveries for validation, but don't reinitialize the form
        this.setDiscoveries(data.discoveries);

        // Rebuild fields to show new discovery messages
        // Flatten payload to check for nested/array properties
        const flattenedPayload = this.flattenObject(data.payload.payload);
        const missingDiscoveries = data.discoveries.filter((d) => {
            return (
                d.location?.payloadFieldPath &&
                !(d.location.payloadFieldPath in flattenedPayload)
            );
        });

        this.fields = [
            this.createSubHeader("HTTP Call Details"),
            ...this.createCallFields(),
            this.createSubHeader("HTTP Headers"),
            ...this.headersToFormlyFields(),
            ...this.discoveriesToFormlyFields(missingDiscoveries),
            this.createSubHeader("Request Payload"),
            ...this.payloadToFormlyFields(),
        ];

        // Manually trigger validation on the form to show new errors
        this.form.updateValueAndValidity();
        Object.keys(this.form.controls).forEach((key) => {
            this.form.get(key)?.updateValueAndValidity();
        });
    }

    private payloadToFormlyModel(payload: any): any[] {
        if (payload == null) return [];
        const model = [];
        const flattenedPayload = this.flattenObject(payload);
        Object.entries(flattenedPayload).forEach(([key, value]) => {
            model.push({
                fieldName: key,
                fieldValue:
                    typeof value === "object" ? JSON.stringify(value) : value,
            });
        });
        return model;
    }

    private flattenObject(obj: any, prefix: string = ""): any {
        const flattened: any = {};

        if (Array.isArray(obj)) {
            // Preserve empty arrays
            if (obj.length === 0 && prefix) {
                flattened[prefix] = obj;
                return flattened;
            }

            // Handle arrays - create fields for each index
            obj.forEach((item, index) => {
                const arrayKey = `${prefix}[${index}]`;
                if (item && typeof item === "object") {
                    // Recursively flatten array items
                    Object.assign(
                        flattened,
                        this.flattenObject(item, arrayKey),
                    );
                } else {
                    flattened[arrayKey] = item;
                }
            });
        } else if (obj && typeof obj === "object") {
            // Handle objects
            for (const [key, value] of Object.entries(obj)) {
                const newKey = prefix ? `${prefix}.${key}` : key;

                if (Array.isArray(value)) {
                    // Flatten arrays with bracket notation
                    Object.assign(flattened, this.flattenObject(value, newKey));
                } else if (value && typeof value === "object") {
                    // Recursively flatten nested objects
                    Object.assign(flattened, this.flattenObject(value, newKey));
                } else {
                    // Primitives
                    flattened[newKey] = value;
                }
            }
        } else {
            // Primitive at root level (shouldn't normally happen)
            flattened[prefix] = obj;
        }

        return flattened;
    }

    private unflattenObject(flattened: any): any {
        const result: any = {};

        for (const [key, value] of Object.entries(flattened)) {
            this.setNestedValue(result, key, value);
        }

        return result;
    }

    private setNestedValue(obj: any, path: string, value: any): void {
        // Parse path like "propertyA[0].type" or "nested.prop" or "array[1]"
        const parts: Array<{ key: string; isArray: boolean; index?: number }> =
            [];
        let current = "";

        for (let i = 0; i < path.length; i++) {
            const char = path[i];

            if (char === "[") {
                // Found array notation
                if (current) {
                    parts.push({ key: current, isArray: false });
                    current = "";
                }
                // Extract index
                const closeIdx = path.indexOf("]", i);
                const index = parseInt(path.substring(i + 1, closeIdx), 10);
                parts.push({ key: "", isArray: true, index });
                i = closeIdx; // Skip to after ]
            } else if (char === ".") {
                // Found object property separator
                if (current) {
                    parts.push({ key: current, isArray: false });
                    current = "";
                }
            } else {
                current += char;
            }
        }

        // Add remaining part
        if (current) {
            parts.push({ key: current, isArray: false });
        }

        // Navigate and create structure
        let target = obj;

        for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i];
            const nextPart = parts[i + 1];

            if (part.isArray) {
                // Current part is an array index
                if (!Array.isArray(target)) {
                    throw new Error(`Expected array at path segment ${i}`);
                }

                // Ensure array has enough elements
                while (target.length <= part.index!) {
                    target.push(nextPart.isArray ? [] : {});
                }

                target = target[part.index!];
            } else {
                // Current part is an object key
                if (!target[part.key]) {
                    // Create array or object based on next part
                    target[part.key] = nextPart.isArray ? [] : {};
                }
                target = target[part.key];
            }
        }

        // Set final value
        const lastPart = parts[parts.length - 1];

        // Try to parse JSON strings back to objects/arrays
        let finalValue = value;
        if (typeof value === "string") {
            try {
                finalValue = JSON.parse(value);
            } catch {
                // Keep as string if not valid JSON
                finalValue = value;
            }
        }

        if (lastPart.isArray) {
            // Setting array element
            if (!Array.isArray(target)) {
                throw new Error("Expected array for final array index");
            }
            while (target.length <= lastPart.index!) {
                target.push(null);
            }
            target[lastPart.index!] = finalValue;
        } else {
            // Setting object property
            target[lastPart.key] = finalValue;
        }
    }

    /**
     * Formats a discovery's error code plus its optional error message
     * (e.g. "invalid_value - Human readable detail") for display under a field.
     */
    private formatDiscoveryError(discovery: ErrorDiscovery): string {
        return discovery.errorMessage
            ? `${discovery.errorCode} - ${discovery.errorMessage}`
            : discovery.errorCode;
    }

    private discoveriesToFormlyFields(
        discoveries: ErrorDiscovery[],
    ): FormlyFieldConfig[] {
        return discoveries.map((discovery) => {
            return {
                className: "discovery-message",
                template: `<p style="color: red;"><strong>Tracy has reported missing field "${
                    discovery.location?.payloadFieldPath
                }"; ${discovery.errorCode ?? "no error code"} - ${
                    discovery.errorMessage ?? ""
                }</strong></p>`,
            };
        });
    }

    private payloadToFormlyFields(): FormlyFieldConfig[] {
        return [
            {
                key: "payloadFields",
                type: "repeat",
                templateOptions: {
                    addText: "Add payload field",
                    hideRemove: true,
                },
                fieldArray: {
                    fieldGroupClassName: "payload-field-group",
                    fieldGroup: [
                        {
                            type: "input",
                            key: "fieldName",
                            templateOptions: {
                                label: "Field Name",
                                required: true,
                                disabled: !this.isFieldEditingEnabled,
                            },
                        },
                        {
                            type: "input",
                            key: "fieldValue",
                            templateOptions: {
                                label: "Field Value",
                                required: true,
                            },
                            validation: {
                                messages: {
                                    discovery: (error, field) => {
                                        // Get the field name from the parent group
                                        const fieldName =
                                            field.parent?.model?.fieldName;
                                        if (!fieldName) return "";

                                        // Find discoveries for this field
                                        const discoveries =
                                            this.payloadDiscoveries.filter(
                                                (d) =>
                                                    d.location
                                                        ?.payloadFieldPath ===
                                                    fieldName,
                                            );
                                        return `Error from Tracy: ${discoveries
                                            .map((d) =>
                                                this.formatDiscoveryError(d),
                                            )
                                            .join(", ")}`;
                                    },
                                },
                            },
                            validators: {
                                discovery: {
                                    expression: (formControl) => {
                                        // Get the field name from the parent group
                                        const fieldName =
                                            formControl.parent?.controls
                                                ?.fieldName?.value;
                                        if (!fieldName) return true;

                                        // Check if there's a discovery for this field
                                        const discoveryExists =
                                            this.payloadDiscoveries.some(
                                                (d) =>
                                                    d.location
                                                        ?.payloadFieldPath ===
                                                    fieldName,
                                            );
                                        return !discoveryExists;
                                    },
                                },
                            },
                        },
                    ],
                },
            },
        ];
    }

    private createSubHeader(text: string): FormlyFieldConfig {
        return {
            className: "section-label",
            template: `<p><strong>${text}</strong></p>`,
        };
    }

    private createCallFields(): FormlyFieldConfig[] {
        return [
            {
                key: "endpoint",
                type: "input",
                templateOptions: {
                    label: "Endpoint",
                    placeholder: "Endpoint",
                    required: true,
                },
                validators: {
                    discovery: {
                        expression: () => {
                            // If there are errors for any pathPart, return false (meaning there is an error).
                            // Otherwise, return true (meaning there is no error).
                            const discoveryExists =
                                this.pathDiscoveries.length > 0;
                            return !discoveryExists;
                        },
                        message: () => {
                            // All path part discoveries need to be displayed together. There is, after all, but one path.
                            // Split the path parts up into an array to be able to select the right one.
                            const pathParts = this.data.call.endpoint
                                .split("/")
                                .filter((part) => part !== "");

                            // Create a nice error message, one for each discovery
                            const formattedDiscoveries =
                                this.pathDiscoveries.map((d) => {
                                    return `Error (part "${
                                        pathParts[d.location.pathPart]
                                    }"): ${this.formatDiscoveryError(d)}`;
                                });
                            return formattedDiscoveries.join("\n");
                        },
                    },
                },
            },
            {
                key: "method",
                type: "input",
                templateOptions: {
                    label: "HTTP Method",
                    placeholder: "HTTP Method",
                    required: true,
                },
            },
        ];
    }

    private headersToFormlyModel(headers: ErrorHeader[]) {
        const model = [];

        headers.forEach((h) => {
            model.push({
                headerName: h.name,
                headerValue: h.value,
            });
        });
        return model;
    }

    private headersToFormlyFields(): FormlyFieldConfig[] {
        return [
            {
                key: "headers",
                type: "repeat",
                templateOptions: {
                    addText: "Add header",
                },
                fieldArray: {
                    fieldGroupClassName: "header-field-group",
                    fieldGroup: [
                        {
                            type: "input",
                            key: "headerName",
                            templateOptions: {
                                label: "Header Name",
                                required: true,
                                disabled: !this.isFieldEditingEnabled,
                            },
                        },
                        {
                            type: "input",
                            key: "headerValue",
                            templateOptions: {
                                label: "Header Value",
                                required: true,
                            },
                            validation: {
                                messages: {
                                    discovery: (error, field) => {
                                        const headerName =
                                            field.parent?.model?.headerName;
                                        if (!headerName) return "";

                                        const discoveries =
                                            this.headerDiscoveries.filter(
                                                (d) =>
                                                    d.location?.headerName ===
                                                    headerName,
                                            );
                                        return `Error from Tracy: ${discoveries
                                            .map((d) =>
                                                this.formatDiscoveryError(d),
                                            )
                                            .join(", ")}`;
                                    },
                                },
                            },
                            validators: {
                                discovery: {
                                    expression: (formControl) => {
                                        // If there is a matching error discovery for this header, return false (meaning there is an error).
                                        // Otherwise, return true (meaning there is no error).
                                        const headerName =
                                            formControl.parent?.controls
                                                ?.headerName?.value;
                                        if (!headerName) return true;

                                        const discoveryExists =
                                            this.headerDiscoveries.some(
                                                (d) =>
                                                    d.location?.headerName ===
                                                    headerName,
                                            );
                                        return !discoveryExists;
                                    },
                                },
                            },
                        },
                    ],
                },
            },
        ];
    }

    public async formlySubmit(model: any) {
        // Convert payloadFields array back to payload object
        const flatPayload = {};
        if (model.payloadFields) {
            model.payloadFields.forEach((field: any) => {
                flatPayload[field.fieldName] = field.fieldValue;
            });
        }

        // Unflatten the payload to support nested properties
        const payload = this.unflattenObject(flatPayload);

        const submissionModel = {
            ...model,
            payload: payload,
        };
        delete submissionModel.payloadFields;

        this.updatedError.emit(submissionModel);
    }

    sendEmailClicked() {
        this.sendEmail.emit();
    }

    archiveErrorClicked() {
        this.archiveError.emit();
    }

    unarchiveErrorClicked() {
        this.unarchiveError.emit();
    }

    cancelClicked() {
        this.store.dispatch(ReasoningActions.clearReasonings());
        this.cancel.emit();
    }

    get allowedToUpdate(): boolean {
        return this.loginService.acceptedByPolicy(Policies.CanUpdateErrors);
    }

    toggleFieldEditing() {
        this.isFieldEditingEnabled = !this.isFieldEditingEnabled;

        // Update payload fields config
        const payloadFieldsConfig = this.fields.find(
            (f) => f.key === "payloadFields",
        );
        if (payloadFieldsConfig) {
            // Toggle hideRemove for remove buttons
            if (payloadFieldsConfig.templateOptions) {
                payloadFieldsConfig.templateOptions.hideRemove =
                    !this.isFieldEditingEnabled;
            }

            if (
                payloadFieldsConfig?.fieldArray &&
                typeof payloadFieldsConfig.fieldArray === "object" &&
                "fieldGroup" in payloadFieldsConfig.fieldArray
            ) {
                const fieldNameConfig =
                    payloadFieldsConfig.fieldArray.fieldGroup.find(
                        (f) => f.key === "fieldName",
                    );
                if (fieldNameConfig?.templateOptions) {
                    fieldNameConfig.templateOptions.disabled =
                        !this.isFieldEditingEnabled;
                }
            }
        }

        // Update headers fields config
        const headersFieldsConfig = this.fields.find(
            (f) => f.key === "headers",
        );
        if (
            headersFieldsConfig?.fieldArray &&
            typeof headersFieldsConfig.fieldArray === "object" &&
            "fieldGroup" in headersFieldsConfig.fieldArray
        ) {
            const headerNameConfig =
                headersFieldsConfig.fieldArray.fieldGroup.find(
                    (f) => f.key === "headerName",
                );
            if (headerNameConfig?.templateOptions) {
                headerNameConfig.templateOptions.disabled =
                    !this.isFieldEditingEnabled;
            }
        }

        // Update the actual form controls for payload fields
        const payloadFieldsArray = this.form.get("payloadFields") as any;
        if (payloadFieldsArray?.controls) {
            payloadFieldsArray.controls.forEach((group: any) => {
                const fieldNameControl = group.get("fieldName");
                if (fieldNameControl) {
                    if (this.isFieldEditingEnabled) {
                        fieldNameControl.enable();
                    } else {
                        fieldNameControl.disable();
                    }
                }
            });
        }

        // Update the actual form controls for headers
        const headersArray = this.form.get("headers") as any;
        if (headersArray?.controls) {
            headersArray.controls.forEach((group: any) => {
                const headerNameControl = group.get("headerName");
                if (headerNameControl) {
                    if (this.isFieldEditingEnabled) {
                        headerNameControl.enable();
                    } else {
                        headerNameControl.disable();
                    }
                }
            });
        }

        // Update remove buttons disabled state
        setTimeout(() => {
            this.disableRemoveButtons();
        }, 0);
    }
}
